import React, { useEffect, useRef, useState, Children, ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CISCO_GRADIENT = 'linear-gradient(90deg, #3070e7, #16bdeb 30%, #ff007f 75%, #ff9000)';

interface CarouselProps {
  children: ReactNode;
  theme?: 'dark' | 'light';
  ariaLabel?: string;
  itemClassName?: string;
  showDots?: boolean;
  showProgressBar?: boolean;
  snapAlign?: 'start' | 'center';
}

export default function Carousel({
  children,
  theme = 'light',
  ariaLabel,
  itemClassName = 'w-[85%] sm:w-[60%] lg:w-[32%]',
  showDots = true,
  showProgressBar = false,
  snapAlign = 'start',
}: CarouselProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const items = Children.toArray(children);
  const [progress, setProgress] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(items.length > 1);

  const dragState = useRef({ isDown: false, startX: 0, startScroll: 0, moved: false });

  const isDark = theme === 'dark';

  const updateScrollState = () => {
    const el = trackRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const pct = maxScroll > 0 ? el.scrollLeft / maxScroll : 0;
    setProgress(pct);
    setCanScrollPrev(el.scrollLeft > 4);
    setCanScrollNext(el.scrollLeft < maxScroll - 4);

    const children = Array.from(el.children) as HTMLElement[];
    if (children.length) {
      let closest = 0;
      let closestDist = Infinity;
      children.forEach((child, i) => {
        const dist = Math.abs(child.offsetLeft - el.scrollLeft);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      });
      setActiveIndex(closest);
    }
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const onResize = () => updateScrollState();
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const scrollByAmount = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const child = el.children[0] as HTMLElement | undefined;
    const step = child ? child.getBoundingClientRect().width + 20 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  const scrollToIndex = (i: number) => {
    const el = trackRef.current;
    const child = el?.children[i] as HTMLElement | undefined;
    if (el && child) {
      el.scrollTo({ left: child.offsetLeft, behavior: 'smooth' });
    }
  };

  const onPointerDown = (e: React.PointerEvent) => {
    const el = trackRef.current;
    if (!el) return;
    dragState.current = { isDown: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false };
    el.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const el = trackRef.current;
    const state = dragState.current;
    if (!el || !state.isDown) return;
    const dx = e.clientX - state.startX;
    if (Math.abs(dx) > 4) state.moved = true;
    if (state.moved) el.scrollLeft = state.startScroll - dx;
  };

  const endDrag = (e: React.PointerEvent) => {
    const el = trackRef.current;
    if (el) {
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        // ignore release failures
      }
    }
    dragState.current.isDown = false;
  };

  const onClickCapture = (e: React.MouseEvent) => {
    if (dragState.current.moved) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const snapClass = snapAlign === 'center' ? 'snap-center' : 'snap-start';
  const arrowBtnClass = isDark
    ? 'bg-[#0f2035] border border-white/10 text-[#8eafc8] hover:text-white hover:border-[#049fd9]/40'
    : 'bg-white border border-gray-200 text-gray-500 hover:text-gray-900 hover:border-gray-300';
  const dotClass = (active: boolean) =>
    active
      ? isDark
        ? 'w-6 bg-[#049fd9]'
        : 'w-6 bg-[#2563eb]'
      : isDark
      ? 'w-1.5 bg-white/20 hover:bg-white/40'
      : 'w-1.5 bg-gray-300 hover:bg-gray-400';

  return (
    <div className="relative">
      <div
        ref={trackRef}
        role="region"
        aria-label={ariaLabel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onClickCapture={onClickCapture}
        className="no-scrollbar flex gap-5 overflow-x-auto snap-x snap-mandatory scroll-smooth cursor-grab active:cursor-grabbing select-none"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {items.map((child, i) => (
          <div key={i} className={`shrink-0 snap-always ${snapClass} ${itemClassName}`}>
            {child}
          </div>
        ))}
      </div>

      {(canScrollPrev || canScrollNext) && (
        <>
          <button
            type="button"
            aria-label="Previous"
            onClick={() => scrollByAmount(-1)}
            disabled={!canScrollPrev}
            className={`hidden sm:flex absolute -left-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full items-center justify-center shadow-lg transition-opacity ${arrowBtnClass} ${
              canScrollPrev ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            aria-label="Next"
            onClick={() => scrollByAmount(1)}
            disabled={!canScrollNext}
            className={`hidden sm:flex absolute -right-4 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full items-center justify-center shadow-lg transition-opacity ${arrowBtnClass} ${
              canScrollNext ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </>
      )}

      {showProgressBar && items.length > 1 && (
        <div className={`mt-6 h-1 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
          <div
            className="h-full rounded-full transition-[width] duration-150 ease-out"
            style={{ width: `${Math.max(8, progress * 100)}%`, background: CISCO_GRADIENT }}
          />
        </div>
      )}

      {showDots && items.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-5">
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => scrollToIndex(i)}
              className={`h-1.5 rounded-full transition-all ${dotClass(i === activeIndex)}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
